import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchFormsTemplateDetail, useFormsTemplateList } from '@/hooks/useFormsTemplates';
import {
  indexQuestionnaireTemplates,
  toFormsLocale,
  type QuestionnaireTemplateName,
} from '@/lib/questionnaireDisplayName';

/** One batched Forms template-list fetch, plus get-by-id for missing unique IDs. */
export function useQuestionnaireNameMap(
  clinicId: string | null | undefined,
  locale?: string,
  templateIds: readonly string[] = [],
) {
  const formsLocale = toFormsLocale(locale);
  const list = useFormsTemplateList(clinicId, formsLocale);

  const fromList = useMemo(() => {
    const rows = [...(list.data?.platform ?? []), ...(list.data?.clinic ?? [])];
    const map = indexQuestionnaireTemplates(rows);
    for (const row of rows) {
      const alias = row.questionnaire_id;
      if (alias && alias !== row.id) {
        map.set(alias, {
          id: row.id,
          display_name_da: row.display_name_da,
          display_name_en: row.display_name_en,
        });
      }
    }
    return map;
  }, [list.data]);

  const missingIds = useMemo(() => {
    const unique = [...new Set(templateIds.filter(Boolean))];
    return unique.filter((id) => !fromList.has(id)).sort();
  }, [templateIds, fromList]);

  const missing = useQuery({
    queryKey: ['forms', 'templates', 'missing', clinicId ?? null, missingIds],
    enabled: Boolean(clinicId) && missingIds.length > 0 && !list.isLoading,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<QuestionnaireTemplateName[]> => {
      const rows = await Promise.all(
        missingIds.map((id) =>
          fetchFormsTemplateDetail(clinicId!, id).catch(() => null),
        ),
      );
      return rows.flatMap((row) => {
        if (!row?.id) return [];
        const entry = {
          id: row.id,
          display_name_da: row.display_name_da,
          display_name_en: row.display_name_en,
        } satisfies QuestionnaireTemplateName;
        const alias = row.template_id;
        if (alias && alias !== row.id) {
          return [entry, { ...entry, id: alias }];
        }
        return [entry];
      });
    },
  });

  const map = useMemo(() => {
    const merged = new Map(fromList);
    for (const row of missing.data ?? []) {
      merged.set(row.id, row);
    }
    return merged;
  }, [fromList, missing.data]);

  return {
    map,
    isLoading: list.isLoading || missing.isLoading,
    isError: list.isError && missing.isError,
  };
}
